"use client";

import type React from "react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { AlertCircle, Anchor, CheckCircle, Clock, Mail, MapPin, MessageSquare, Phone, Send } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import { useBusinessInfo } from "@/lib/use-settings";
import { ContactFormData } from "@/lib/types/contact";

const faqData = [
	// {
	// 	question: "Comment réserver une excursion ?",
	// 	answer: "Vous pouvez réserver directement par téléphone au +33 6 52 73 34 91, ou par email à soleiletdecouverte971@gmail.com. Nous vous recommandons de réserver à l'avance pour garantir votre place.",
	// },
	// {
	// 	question: "Quels sont les tarifs pour les enfants ?",
	// 	answer: "Nos excursions sont adaptées aux familles : gratuit pour les moins de 6 ans pour la rencontre avec les pélicans, gratuit pour les moins de 12 ans pour les visites culturelles, et à partir de 12 ans pour les WaterBikes.",
	// },
	{
		question: "Que se passe-t-il en cas de mauvais temps ?",
		answer: "La sécurité de nos clients est notre priorité. En cas de conditions météorologiques défavorables, nous reportons ou annulons l'excursion. Vous serez remboursé intégralement ou pourrez reporter votre réservation sans frais.",
	},
	{
		question: "Que dois-je apporter pour l'excursion ?",
		answer: "Nous recommandons : crème solaire, chapeau, lunettes de soleil, maillot de bain, serviette, et une bouteille d'eau. Nous fournissons les collations, sacs étanches et jumelles d'observation selon l'excursion.",
	},
	// {
	// 	question: "Vos excursions sont-elles éco-responsables ?",
	// 	answer: "Absolument ! Toutes nos activités sont pensées pour minimiser l'impact sur l'environnement, respecter la biodiversité et sensibiliser à la préservation de notre patrimoine naturel guadeloupéen.",
	// },
	{
		question: "Proposez-vous des dégustations de produits locaux ?",
		answer: "Oui ! Nous proposons des expériences gustatives avec des produits locaux authentiques. Chaque bouchée est une invitation au voyage avec des saveurs que vous ne trouverez nulle part ailleurs.",
	},
	// {
	// 	question: "Où se déroulent vos excursions ?",
	// 	answer: "Nos excursions partent du port de pêche de Petit-Canal et nous emmènent découvrir les îlets secrets, la mangrove, les sites historiques et les plus beaux spots de la Guadeloupe.",
	// },
	{
		question: "Quels équipements fournissez-vous ?",
		answer: "Selon l'excursion : WaterBikes, sacs étanches, jumelles d'observation, collations, et tout l'équipement de sécurité nécessaire. Nos guides expérimentés vous accompagnent pour une expérience en toute sérénité.",
	},
];

interface Service {
	id: string;
	name: string;
	category: string;
}

export default function ContactPage() {
	const [formData, setFormData] = useState({
		name: "",
		email: "",
		phone: "",
		subject: "",
		message: "",
		serviceId: "",
		participants: "",
		honeypot: "", // Anti-bot honeypot field
	});

	// Subject options for the dropdown
	const subjectOptions = [
		{ value: "information", label: "Demande d'information" },
		{ value: "reservation", label: "Question sur une réservation" },
		{ value: "pricing", label: "Question sur les tarifs" },
		{ value: "availability", label: "Vérification de disponibilité" },
		{ value: "group", label: "Réservation de groupe" },
		{ value: "cancellation", label: "Annulation ou modification" },
		{ value: "complaint", label: "Réclamation" },
		{ value: "partnership", label: "Partenariat" },
		{ value: "other", label: "Autre" },
	];
	const [services, setServices] = useState<Service[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const { settings } = useBusinessInfo();

	// Load services for dropdown
	useEffect(() => {
		const fetchServices = async () => {
			try {
				const response = await fetch("/api/services");
				if (response.ok) {
					const data = await response.json();
					setServices(data.services || []);
				}
			} catch (error) {
				console.error("Error loading services:", error);
			}
		};
		fetchServices();
	}, []);

	const validateForm = () => {
		const newErrors: Record<string, string> = {};

		if (!formData.name.trim()) {
			newErrors.name = "Le nom est requis";
		}

		if (!formData.email.trim()) {
			newErrors.email = "L'email est requis";
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = "Format d'email invalide";
		}

		if (!formData.subject.trim()) {
			newErrors.subject = "Le sujet est requis";
		}

		if (!formData.message.trim()) {
			newErrors.message = "Le message est requis";
		} else if (formData.message.length < 10) {
			newErrors.message = "Le message doit contenir au moins 10 caractères";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		// Prevent double submission
		if (isSubmitting) {
			return;
		}

		setIsSubmitting(true);
		setErrors({});

		try {
			const contactData: ContactFormData = {
				name: formData.name,
				email: formData.email,
				phone: formData.phone || undefined,
				subject: formData.subject,
				message: formData.message,
				serviceId: formData.serviceId || undefined,
				participants: formData.participants ? parseInt(formData.participants) : undefined,
			};

			const response = await fetch("/api/contact", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(contactData),
			});

			const result = await response.json();

			if (!response.ok) {
				setErrors({ general: result.error || "Erreur lors de l'envoi du message" });
				return;
			}

			setIsSubmitted(true);

			// Reset form after 3 seconds
			setTimeout(() => {
				setIsSubmitted(false);
				setFormData({
					name: "",
					email: "",
					phone: "",
					subject: "",
					message: "",
					serviceId: "",
					participants: "",
					honeypot: "",
				});
			}, 3000);
		} catch (error) {
			console.error("Error submitting contact form:", error);
			setErrors({ general: "Erreur de connexion. Veuillez réessayer." });
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-b from-sky-50 to-emerald-50">
			{/* Header */}
			<header className="bg-white/90 backdrop-blur-md shadow-sm sticky top-0 z-50">
				<div className="container mx-auto px-4 py-4">
					<div className="flex items-center justify-between">
						<Link href="/" className="flex items-center space-x-3">
							<Image
								src="/images/logo-hd.png"
								alt="Soleil & Découverte"
								width={60}
								height={60}
								className="object-contain"
							/>
							<div>
								<h1 className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
									Soleil & Découverte
								</h1>
								<p className="text-sm text-emerald-600">L'aventure au cœur de la Guadeloupe</p>
							</div>
						</Link>

						<nav className="hidden md:flex items-center space-x-8">
							<Link
								href="/"
								className="text-gray-700 hover:text-emerald-600 transition-colors font-medium"
							>
								Accueil
							</Link>
							<Link
								href="/activites"
								className="text-gray-700 hover:text-emerald-600 transition-colors font-medium"
							>
								Activités
							</Link>
							<Link
								href="/partenaires"
								className="text-gray-700 hover:text-emerald-600 transition-colors font-medium"
							>
								Partenaires
							</Link>
							<Link href="/contact" className="text-emerald-600 font-semibold">
								Contact
							</Link>
							<Link href="/reservation">
								<Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
									Réserver
								</Button>
							</Link>
						</nav>

						{/* Mobile menu button */}
						<Button
							variant="ghost"
							className="md:hidden"
							onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
						>
							<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 6h16M4 12h16M4 18h16"
								/>
							</svg>
						</Button>
					</div>
				</div>

				{/* Mobile Menu */}
				{isMobileMenuOpen && (
					<div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
						<div className="container mx-auto px-4 py-4">
							<nav className="flex flex-col space-y-4">
								<Link
									href="/"
									className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									Accueil
								</Link>
								<Link
									href="/activites"
									className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									Activités
								</Link>
								<Link
									href="/partenaires"
									className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									Partenaires
								</Link>
								<Link
									href="/contact"
									className="text-emerald-600 font-semibold py-2"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									Contact
								</Link>
								<Link href="/reservation" onClick={() => setIsMobileMenuOpen(false)}>
									<Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 w-full">
										Réserver
									</Button>
								</Link>
							</nav>
						</div>
					</div>
				)}
			</header>

			{/* Hero Section */}
			<section className="relative py-20 overflow-hidden">
				<Image
					src="/images/contact_hero.png"
					alt="Contact Hero"
					fill
					className="object-cover"
					style={{ zIndex: 1 }}
				/>
				<div className="absolute inset-0 bg-black/20" style={{ zIndex: 2 }}></div>

				<div className="container mx-auto px-4 relative z-10">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="text-center max-w-4xl mx-auto"
					>
						<Badge className="mb-6 bg-white/20 text-white border-white/30 backdrop-blur-sm px-4 py-2 text-lg">
							📞 Contact & Infos
						</Badge>
						<h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
							Parlons de Votre
							<span className="block bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
								Prochaine Aventure
							</span>
						</h1>
						<p className="text-xl text-white/90 max-w-3xl mx-auto">
							Notre équipe est là pour répondre à toutes vos questions et vous aider à planifier
							l'excursion parfaite en Guadeloupe.
						</p>
					</motion.div>
				</div>
			</section>

			{/* Contact Info - Condensed */}
			<section className="py-8 -mt-12 relative z-10">
				<div className="container mx-auto px-4">
					<div className="max-w-4xl mx-auto">
						<Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
							<CardContent className="p-8">
								<div className="grid md:grid-cols-4 gap-6 text-center">
									<div>
										<Phone className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
										<p className="font-semibold text-gray-900">
											{settings.business_phone || "+33 6 52 73 34 91"}
										</p>
										<p className="text-sm text-gray-500">Tous les jours</p>
									</div>
									<div className="flex flex-col items-center">
										<Mail className="w-6 h-6 text-blue-600 mb-2" />
										<p className="font-semibold text-gray-900 text-sm text-center">
											{settings.business_email || "soleiletdecouverte971@gmail.com"}
										</p>
										<p className="text-sm text-gray-500 text-center">Réponse rapide</p>
									</div>
									<div>
										<MapPin className="w-6 h-6 text-orange-600 mx-auto mb-2" />
										<p className="font-semibold text-gray-900">Port de pêche</p>
										<p className="text-sm text-gray-500">
											{settings.business_address || "Petit-Canal, 97131"}
										</p>
									</div>
									<div>
										<Clock className="w-6 h-6 text-purple-600 mx-auto mb-2" />
										<p className="font-semibold text-gray-900">Flexible</p>
										<p className="text-sm text-gray-500">Selon marées</p>
									</div>
								</div>
							</CardContent>
						</Card>
					</div>
				</div>
			</section>

			{/* Main Content - Condensed */}
			<section className="py-12">
				<div className="container mx-auto px-4">
					<div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
						{/* Contact Form - Takes 2 columns */}
						<div className="lg:col-span-2">
							<Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
								<CardHeader>
									<CardTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
										<MessageSquare className="w-6 h-6 text-emerald-600" />
										Contactez-nous
									</CardTitle>
									<p className="text-gray-600">
										Remplissez ce formulaire et nous vous répondrons dans les plus brefs délais.
									</p>
								</CardHeader>
								<CardContent>
									{isSubmitted ? (
										<motion.div
											initial={{ opacity: 0, scale: 0.9 }}
											animate={{ opacity: 1, scale: 1 }}
											className="text-center py-8"
										>
											<CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
											<h3 className="text-xl font-semibold text-gray-900 mb-2">
												Message envoyé !
											</h3>
											<p className="text-gray-600">
												Merci pour votre message. Nous vous répondrons rapidement.
											</p>
										</motion.div>
									) : (
										<form onSubmit={handleSubmit} className="space-y-4">
											{/* Honeypot field - hidden from users, should remain empty */}
											<input
												type="text"
												name="website"
												value={formData.honeypot}
												onChange={(e) => handleInputChange("honeypot", e.target.value)}
												style={{ display: "none" }}
												tabIndex={-1}
												autoComplete="off"
											/>

											{errors.general && (
												<div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
													<p className="text-red-600 text-sm flex items-center gap-2">
														<AlertCircle className="w-4 h-4" />
														{errors.general}
													</p>
												</div>
											)}
											<div className="grid md:grid-cols-2 gap-4">
												<div>
													<label
														htmlFor="name"
														className="block text-sm font-medium text-gray-700 mb-1"
													>
														Nom complet *
													</label>
													<Input
														id="name"
														value={formData.name}
														onChange={(e) => handleInputChange("name", e.target.value)}
														className={`${errors.name ? "border-red-500" : ""}`}
														placeholder="Votre nom"
													/>
													{errors.name && (
														<p className="text-red-500 text-sm mt-1 flex items-center gap-1">
															<AlertCircle className="w-4 h-4" />
															{errors.name}
														</p>
													)}
												</div>

												<div>
													<label
														htmlFor="email"
														className="block text-sm font-medium text-gray-700 mb-1"
													>
														Email *
													</label>
													<Input
														id="email"
														type="email"
														value={formData.email}
														onChange={(e) => handleInputChange("email", e.target.value)}
														className={`${errors.email ? "border-red-500" : ""}`}
														placeholder="votre@email.com"
													/>
													{errors.email && (
														<p className="text-red-500 text-sm mt-1 flex items-center gap-1">
															<AlertCircle className="w-4 h-4" />
															{errors.email}
														</p>
													)}
												</div>
											</div>

											<div className="grid md:grid-cols-2 gap-4">
												<div>
													<label
														htmlFor="phone"
														className="block text-sm font-medium text-gray-700 mb-1"
													>
														Téléphone
													</label>
													<Input
														id="phone"
														value={formData.phone}
														onChange={(e) => handleInputChange("phone", e.target.value)}
														placeholder="+33 6 XX XX XX XX"
													/>
												</div>

												<div>
													<label
														htmlFor="participants"
														className="block text-sm font-medium text-gray-700 mb-1"
													>
														Nombre de participants
													</label>
													<Input
														id="participants"
														type="number"
														min="1"
														value={formData.participants}
														onChange={(e) =>
															handleInputChange("participants", e.target.value)
														}
														placeholder="2"
													/>
												</div>
											</div>

											<div className="grid md:grid-cols-2 gap-4">
												<div>
													<label
														htmlFor="subject"
														className="block text-sm font-medium text-gray-700 mb-1"
													>
														Sujet *
													</label>
													<Select
														value={formData.subject || undefined}
														onValueChange={(value) => handleInputChange("subject", value)}
													>
														<SelectTrigger
															className={`${errors.subject ? "border-red-500" : ""}`}
														>
															<SelectValue placeholder="Sélectionnez le sujet de votre message" />
														</SelectTrigger>
														<SelectContent>
															{subjectOptions.map((option) => (
																<SelectItem key={option.value} value={option.value}>
																	{option.label}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
													{errors.subject && (
														<p className="text-red-500 text-sm mt-1 flex items-center gap-1">
															<AlertCircle className="w-4 h-4" />
															{errors.subject}
														</p>
													)}
												</div>

												<div>
													<label
														htmlFor="serviceId"
														className="block text-sm font-medium text-gray-700 mb-1"
													>
														Activité souhaitée
													</label>
													<Select
														value={formData.serviceId}
														onValueChange={(value) => handleInputChange("serviceId", value)}
													>
														<SelectTrigger>
														<SelectValue placeholder="Sélectionnez une activité (optionnel)" />
														</SelectTrigger>
														<SelectContent>
															{services.map((service) => (
																<SelectItem key={service.id} value={service.id}>
																	{service.name}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
											</div>

											<div>
												<label
													htmlFor="message"
													className="block text-sm font-medium text-gray-700 mb-1"
												>
													Message *
												</label>
												<Textarea
													id="message"
													rows={4}
													value={formData.message}
													onChange={(e) => handleInputChange("message", e.target.value)}
													className={`${errors.message ? "border-red-500" : ""}`}
													placeholder="Décrivez votre demande..."
												/>
												{errors.message && (
													<p className="text-red-500 text-sm mt-1 flex items-center gap-1">
														<AlertCircle className="w-4 h-4" />
														{errors.message}
													</p>
												)}
											</div>

											<Button
												type="submit"
												disabled={isSubmitting}
												className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
											>
												{isSubmitting ? (
													<div className="flex items-center gap-2">
														<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
														Envoi en cours...
													</div>
												) : (
													<div className="flex items-center gap-2">
														<Send className="w-4 h-4" />
														Envoyer le message
													</div>
												)}
											</Button>
										</form>
									)}
								</CardContent>
							</Card>
						</div>

						{/* Right Sidebar - Takes 1 column */}
						<div className="space-y-6">
							{/* Embedded Map */}
							<Card className="shadow-xl border-0 bg-white/95 backdrop-blur-sm overflow-hidden">
								<div className="relative h-64">
									<iframe
										src="https://maps.google.com/maps?width=100%25&amp;height=600&amp;hl=en&amp;q=Port%20de%20p%C3%AAche,%20Esc.%20aux%20Esclaves,%20Petit-Canal%2097131,%20Guadeloupe+(My%20Business%20Name)&amp;t=&amp;z=16&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
										width="100%"
										height="100%"
										style={{ border: 0 }}
										allowFullScreen
										loading="lazy"
										referrerPolicy="no-referrer-when-downgrade"
										className="rounded-t-xl"
									/>
								</div>
								<div className="p-4">
									<h3 className="font-semibold text-gray-900 mb-1">Port de pêche de Petit-Canal</h3>
									<p className="text-sm text-gray-600">
										{settings.business_address || "Rue de la Darse, 97131 Guadeloupe"}
									</p>
								</div>
							</Card>

							{/* Infos Pratiques - Condensed */}
							<Card className="shadow-xl border-0 bg-white/95 backdrop-blur-sm">
								<CardContent className="p-6">
									<h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
										<Anchor className="w-5 h-5 text-emerald-600" />
										Infos Pratiques
									</h3>
									<div className="space-y-3 text-sm">
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Réservation</span>
											<span className="font-medium text-emerald-600">Recommandée</span>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Enfants</span>
											<span className="font-medium text-emerald-600">Tarifs préférentiels</span>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Équipement</span>
											<span className="font-medium text-emerald-600">Fourni</span>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Éco-responsable</span>
											<span className="font-medium text-emerald-600">Une priorité </span>
										</div>
									</div>
								</CardContent>
							</Card>
						</div>
					</div>
				</div>
			</section>

			{/* FAQ Section */}
			<section className="py-20 bg-white">
				<div className="container mx-auto px-4">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="text-center mb-16"
					>
						<Badge className="mb-4 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 border-blue-200 px-4 py-2">
							❓ Questions Fréquentes
						</Badge>
						<h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
							Tout Ce Que Vous
							<span className="block text-emerald-600">Devez Savoir</span>
						</h2>
						<p className="text-xl text-gray-600 max-w-3xl mx-auto">
							Retrouvez les réponses aux questions les plus fréquemment posées par nos clients.
						</p>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, y: 50 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="max-w-4xl mx-auto"
					>
						<Accordion type="single" collapsible className="space-y-4">
							{faqData.map((faq, index) => (
								<AccordionItem
									key={index}
									value={`item-${index}`}
									className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border-0 px-6"
								>
									<AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-emerald-600 transition-colors py-6">
										{faq.question}
									</AccordionTrigger>
									<AccordionContent className="text-gray-600 leading-relaxed pb-6">
										{faq.answer}
									</AccordionContent>
								</AccordionItem>
							))}
						</Accordion>
					</motion.div>
				</div>
			</section>

			{/* CTA Section */}
			<section className="py-20 bg-gradient-to-br from-emerald-600 to-teal-700 relative overflow-hidden">
				<div className="container mx-auto px-4 relative z-10">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="text-center max-w-4xl mx-auto"
					>
						<h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Prêt à Partir à l'Aventure ?</h2>
						<p className="text-xl text-white/90 mb-8">
							N'attendez plus ! Contactez-nous dès maintenant pour réserver votre excursion de rêve en
							Guadeloupe.
						</p>
						<div className="flex flex-col sm:flex-row gap-4 justify-center">
							<Link href="/reservation">
								<Button
									size="lg"
									className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 text-lg font-semibold"
								>
									Nous Contacter
								</Button>
							</Link>
						</div>
					</motion.div>
				</div>
			</section>

			<Footer />
		</div>
	);
}
